import { useState } from 'react';
import { PageDealType } from '@types';
import { sanitizeSlug } from '@components/AdminSlugActions/utils';

export interface handleOpenModalPropsOptions {
  type?: string;
  title?: string;
  menuTitle?: string;
  unsanitizedSlug?: string;
  // This is a dealListId
  dealListId?: number;
  brand?: string;
  category?: string;
  second_category?: string;
}

export const usePageDealState = (initialPageDeal: PageDealType | null) => {
  const [dealListId, setDealListId] = useState<number>(null);
  const [menuTitle, setMenuTitle] = useState<string>('');
  const [imageLarge, setImageLarge] = useState<string>('');
  const [imageSmall, setImageSmall] = useState<string>('');
  const [selectedPageDeal, setSelectedPageDeal] = useState<PageDealType | null>(
    initialPageDeal
  );
  const [type, setType] = useState('');
  const [ASINsToRemove, setASINsToRemove] = useState<string[]>([]);
  const [tag, setTag] = useState('');
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [showSort, setShowSort] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [mailingListSignUpUrl, setMailingListSignUpUrl] = useState<string>('');
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [showBrandFilter, setShowBrandFilter] = useState(false);
  const [showCouponFilter, setShowCouponFilter] = useState(false);
  const [strategyId, setStrategyId] = useState<string>('');
  const [activeTimeFrame, setActiveTimeFrame] = useState<string>('ALL');
  const [showSecondaryCategoryFilter, setShowSecondaryCategoryFilter] =
    useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [internalLinkText, setInternalLinkText] = useState('');
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [keyword, setKeyword] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [postDate, setPostDate] = useState<Date>(new Date());

  const setDataToPageDeal = (pageDeal: PageDealType) => {
    setType(pageDeal.type);
    setDealListId(pageDeal.dealListId);
    setMenuTitle(pageDeal.menuTitle);
    setImageLarge(pageDeal.imageLarge);
    setImageSmall(pageDeal.imageSmall);
    setTag(pageDeal.tag);
    setSlug(pageDeal.slug);
    setTitle(pageDeal.rawTitle || pageDeal.title);
    setShowSort(pageDeal.showSort);
    setIsActive(pageDeal.isActive);
    setShowCategoryFilter(pageDeal.showCategoryFilter);
    setShowBrandFilter(pageDeal.showBrandFilter);
    setShowCouponFilter(pageDeal.showCouponFilter);
    setStrategyId(pageDeal.strategyId);
    setShowSecondaryCategoryFilter(pageDeal.showSecondaryCategoryFilter);
    setShowSearch(pageDeal.showSearch);
    setBrand(pageDeal.brand);
    setCategory(pageDeal.category);
    setDescription(pageDeal.description);
    setMailingListSignUpUrl(pageDeal.mailingListSignUpUrl);
    setKeyword(pageDeal.keyword);
    setActiveTimeFrame(pageDeal.activeTimeFrame);
    setPostDate(new Date(pageDeal.postDate));
    setInternalLinkText(pageDeal.internalLinkText);
  };

  const setEmptyData = () => {
    setImageLarge('');
    setImageSmall('');
    setTag('jd-sh-20');
    setShowSort(true);
    setIsActive(true);
    setShowCategoryFilter(false);
    setShowBrandFilter(false);
    setShowCouponFilter(false);
    setShowSecondaryCategoryFilter(false);
    setShowSearch(false);
    setDescription('');
    setMailingListSignUpUrl('');
    setKeyword('');
    setActiveTimeFrame('ALL');
    setPostDate(new Date());
    setStrategyId('');
    setInternalLinkText('');
  };

  const getUpdateData = () => {
    return {
      title,
      slug,
      description: description || null,
      type,
      tag: tag || null,
      imageLarge: imageLarge || null,
      imageSmall: imageSmall || null,
      menuTitle,
      showSort,
      isActive,
      showCategoryFilter,
      showBrandFilter,
      showCouponFilter,
      strategyId,
      mailingListSignUpUrl: mailingListSignUpUrl || null,
      showSecondaryCategoryFilter,
      showSearch,
      brand: brand || null,
      id: selectedPageDeal?.id || null,
      dealListId: dealListId || null,
      category: category || null,
      keyword: keyword || null,
      activeTimeFrame,
      internalLinkText,
      postDate
    };
  };

  const setEmptyDataWithOptions = (options?: handleOpenModalPropsOptions) => {
    if (options?.type) {
      setType(options.type);
    } else {
      setType(null);
    }

    if (options?.title) {
      setTitle(options.title);
    } else {
      setTitle(null);
    }

    if (options?.menuTitle) {
      setMenuTitle(options.menuTitle);
    } else {
      setMenuTitle(null);
    }

    if (options?.unsanitizedSlug) {
      setSlug(sanitizeSlug(options?.unsanitizedSlug));
    } else {
      setSlug('');
    }

    if (options?.dealListId) {
      setDealListId(options.dealListId);
    } else {
      setDealListId(null);
    }

    if (options?.brand) {
      setBrand(options.brand);
    } else {
      setBrand('');
    }

    if (options?.category || options?.second_category) {
      const category = options?.category || options?.second_category;
      setCategory(category);
    } else {
      setCategory('');
    }

    setEmptyData();
  };

  const handleOpenModal = (
    pageDeal: PageDealType | null,
    options?: handleOpenModalPropsOptions
  ) => {
    setSelectedPageDeal(pageDeal);
    setOpenModal(true);

    if (pageDeal) {
      setDataToPageDeal(pageDeal);
    } else {
      setEmptyDataWithOptions(options);
    }
  };

  return {
    dealListId,
    setDealListId,
    menuTitle,
    setMenuTitle,
    imageLarge,
    setImageLarge,
    imageSmall,
    setImageSmall,
    selectedPageDeal,
    setSelectedPageDeal,
    type,
    setType,
    ASINsToRemove,
    setASINsToRemove,
    tag,
    setTag,
    slug,
    setSlug,
    title,
    setTitle,
    description,
    setDescription,
    showSort,
    setShowSort,
    isActive,
    setIsActive,
    mailingListSignUpUrl,
    setMailingListSignUpUrl,
    showCategoryFilter,
    showBrandFilter,
    setShowCategoryFilter,
    setShowBrandFilter,
    showCouponFilter,
    setShowCouponFilter,
    strategyId,
    setStrategyId,
    activeTimeFrame,
    setActiveTimeFrame,
    showSecondaryCategoryFilter,
    setShowSecondaryCategoryFilter,
    showSearch,
    setShowSearch,
    internalLinkText,
    setInternalLinkText,
    brand,
    setBrand,
    category,
    setCategory,
    keyword,
    setKeyword,
    openModal,
    setOpenModal,
    postDate,
    setPostDate,
    setDataToPageDeal,
    setEmptyData,
    setEmptyDataWithOptions,
    getUpdateData,
    handleOpenModal
  };
};
